import React, { useState } from 'react';
import {
  signIn, confirmSignIn, confirmSignUp, resendSignUpCode,
  resetPassword, confirmResetPassword, fetchMFAPreference,
} from 'aws-amplify/auth';
import uiaaLogo from '../uiaa-logo-white.png';
import portalLogo from '../the217_logo_transparent.svg';
import heroBg from '../hero-2.JPG';

function getRequirements(password) {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Uppercase letter',       met: /[A-Z]/.test(password) },
    { label: 'Lowercase letter',       met: /[a-z]/.test(password) },
    { label: 'Number',                 met: /[0-9]/.test(password) },
    { label: 'Symbol',                 met: /[^A-Za-z0-9]/.test(password) },
  ];
}

function allMet(password) {
  return getRequirements(password).every(r => r.met);
}

function PasswordRequirements({ password }) {
  if (!password) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {getRequirements(password).map(r => (
        <div key={r.label} className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${r.met ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span className={`text-[11px] transition-colors ${r.met ? 'text-green-600' : 'text-gray-400'}`}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

function MfaNudge({ onEnroll, onSkip }) {
  return (
    <div className="space-y-5">
      <div className="flex justify-center">
        <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 text-orange-500">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
          </svg>
        </div>
      </div>
      <div className="text-center space-y-1.5">
        <p className="font-semibold text-sm text-gray-900">Set up two-factor authentication</p>
        <p className="text-xs leading-relaxed text-gray-500">Add an extra layer of security using an authenticator app like Duo, Google Authenticator, or Authy.</p>
      </div>
      <div className="space-y-2.5">
        <FormButton onClick={onEnroll}>Set Up Now</FormButton>
        <button type="button" onClick={onSkip} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors pt-1">Not now</button>
      </div>
    </div>
  );
}

const inputBase = 'w-full h-11 px-4 rounded-xl text-sm border border-gray-200 bg-white text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500';
const labelBase = 'block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5';

function FormInput({ label, className = '', ...props }) {
  return (
    <div>
      {label && <label className={labelBase}>{label}</label>}
      <input className={`${inputBase} ${className}`} {...props} />
    </div>
  );
}

function FormPasswordInput({ label, value, onChange, placeholder, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && <label className={labelBase}>{label}</label>}
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${inputBase} pr-12`}
          {...props}
        />
        <button type="button" onClick={() => setShow(s => !s)} tabIndex={-1} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
          {show ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

function FormButton({ loading = false, disabled = false, children, ...props }) {
  return (
    <button
      disabled={loading || disabled}
      className="w-full h-11 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]"
      {...props}
    >
      {loading ? (
        <span className="inline-flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          {children}
        </span>
      ) : children}
    </button>
  );
}

function Banner({ type, children }) {
  if (!children) return null;
  const styles = {
    error:   'bg-red-50 border-red-200 text-red-700',
    success: 'bg-green-50 border-green-200 text-green-700',
  };
  return <div className={`mb-4 px-3.5 py-2.5 rounded-xl border text-xs leading-relaxed ${styles[type]}`}>{children}</div>;
}

export default function Login() {
  const [email,            setEmail]            = useState('');
  const [password,         setPassword]         = useState('');
  const [mode,             setMode]             = useState('signin');
  const [confirmPassword,  setConfirmPassword]  = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword,      setNewPassword]      = useState('');
  const [resetCode,        setResetCode]        = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [totpCode,         setTotpCode]         = useState('');
  const [error,            setError]            = useState('');
  const [loading,          setLoading]          = useState(false);
  const [success,          setSuccess]          = useState('');

  function reset() { setError(''); setSuccess(''); }

  async function checkMfaAndProceed() {
    try {
      const pref = await fetchMFAPreference();
      if (pref?.enabled?.includes('TOTP')) window.location.reload();
      else setMode('mfa-nudge');
    } catch { window.location.reload(); }
  }

  const handleSignIn = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try {
      const result = await signIn({ username: email, password });
      const next = result.nextStep?.signInStep;
      if (next === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') setMode('mfa');
      else if (next === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') setMode('newpassword');
      else if (next === 'DONE') await checkMfaAndProceed();
    } catch (err) {
      if (err.name === 'UserAlreadyAuthenticatedException') window.location.reload();
      else setError(err.message || 'Sign in failed');
    } finally { setLoading(false); }
  };

  const handleConfirmMfa = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try { await confirmSignIn({ challengeResponse: totpCode }); window.location.reload(); }
    catch (err) { setError(err.message || 'Invalid code. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault(); reset();
    if (!allMet(newPassword)) { setError('Please meet all password requirements.'); return; }
    setLoading(true);
    try { await confirmSignIn({ challengeResponse: newPassword }); await checkMfaAndProceed(); }
    catch (err) { setError(err.message || 'Failed to set new password'); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try { await confirmSignUp({ username: email, confirmationCode: verificationCode }); await signIn({ username: email, password }); window.location.reload(); }
    catch (err) { setError(err.message || 'Verification failed'); }
    finally { setLoading(false); }
  };

  const handleResendCode = async () => {
    try { await resendSignUpCode({ username: email }); }
    catch (err) { setError(err.message); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); reset();
    if (!email.trim()) { setError('Please enter your email address first.'); return; }
    setLoading(true);
    try { await resetPassword({ username: email }); setSuccess(`A reset code was sent to ${email}.`); setMode('forgot-confirm'); }
    catch (err) { setError(err.message || 'Failed to send reset code.'); }
    finally { setLoading(false); }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault(); reset();
    if (!allMet(resetNewPassword)) { setError('Please meet all password requirements.'); return; }
    if (resetNewPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: resetCode, newPassword: resetNewPassword });
      setSuccess('Password reset successfully. You can now sign in.');
      setMode('signin'); setResetCode(''); setResetNewPassword(''); setConfirmPassword('');
    } catch (err) { setError(err.message || 'Failed to reset password.'); }
    finally { setLoading(false); }
  };

  const handleEnrollMfa = () => {
    sessionStorage.setItem('redirectAfterLogin', '/profile?tab=security');
    window.location.reload();
  };

  const subtitles = {
    'signin': 'Sign in to your account', 'mfa': 'Two-factor authentication',
    'newpassword': 'Set a new password', 'forgot': 'Reset your password',
    'forgot-confirm': 'Enter the reset code', 'verify': 'Verify your email',
    'mfa-nudge': 'Secure your account',
  };

  return (
    <div className="h-screen w-screen flex overflow-hidden">

      {/* Left: Hero — desktop only */}
      <div
        className="hidden lg:flex lg:w-[58%] xl:w-[62%] relative flex-col justify-between p-10 shrink-0"
        style={{ backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,20,40,0.6) 0%, rgba(10,20,40,0.3) 60%, rgba(10,20,40,0.15) 100%)' }} />
        <div className="relative z-10">
          <img src={uiaaLogo} alt="UIAA" className="h-20 w-auto object-contain" />
        </div>
        {/* <div className="relative z-10">
          <img src={portalLogo} alt="The 217" className="h-16 w-auto object-contain mb-3" />
          <p className="text-white/60 text-sm max-w-xs leading-relaxed">UIAA Advancement Data &amp; IT — Internal Staff Portal</p>
        </div> */}
      </div>

      {/* Right: Auth panel — white on desktop, hero on mobile */}
      <div className="flex-1 relative flex flex-col h-full overflow-y-auto bg-white">

        {/* Mobile hero bg */}
        <div className="lg:hidden absolute inset-0" style={{ backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="lg:hidden absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,20,40,0.5) 0%, rgba(10,20,40,0.88) 100%)' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full py-12">
          <div className="w-full max-w-sm px-8 lg:px-0">
            {/* Mobile logo */}
            {/* <div className="lg:hidden mb-10 flex justify-center">
              <img src={portalLogo} alt="The 217" className="h-14 w-auto object-contain" />
            </div> */}

            {/* Title */}
            <div className="mb-8">
              {/* <h1 className="text-md font-semibold tracking-tight text-gray-900 lg:text-gray-900 [.mobile-mode_&]:text-white mb-2">
                Alumni Information Services'
              </h1> */}
              <span style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
      fontSize: '40px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1,
      display: 'flex',
      alignItems: 'baseline',
      gap: '1px',
    }}>
      <span style={{ color: 'var(--color-blue)', fontWeight: 700 }}>Project</span>
      <span style={{ color: 'rgb(167, 167, 167)', fontWeight: 300 }}>&nbsp;(</span>
      <span style={{ color: 'var(--color-orange)', fontWeight: 700 }}>217</span>
      <span style={{ color: 'rgb(167, 167, 167)', fontWeight: 300 }}>)</span>
    </span>
              <p className="text-md mt-4 text-gray-800">{subtitles[mode]}</p>
            </div>

            <Banner type="error">{error}</Banner>
            <Banner type="success">{success}</Banner>

            {mode === 'signin' && (
              <form onSubmit={handleSignIn} className="space-y-4">
                <FormInput label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="netid@illinois.edu" required />
                <FormPasswordInput label="Password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
                <FormButton loading={loading}>{loading ? 'Signing in...' : 'Sign In'}</FormButton>
                <button type="button" onClick={() => { setMode('forgot'); reset(); }} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors pt-1">Forgot password?</button>
              </form>
            )}

            {mode === 'mfa' && (
              <form onSubmit={handleConfirmMfa} className="space-y-4">
                <p className="text-xs text-gray-500 leading-relaxed">Open your authenticator app (Duo, Google Authenticator, or Authy) and enter the 6-digit code.</p>
                <FormInput label="Authentication Code" type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} autoFocus className="tracking-[0.4em] text-center font-mono text-lg" />
                <FormButton loading={loading} disabled={totpCode.length !== 6}>{loading ? 'Verifying...' : 'Verify Code'}</FormButton>
              </form>
            )}

            {mode === 'newpassword' && (
              <form onSubmit={handleNewPassword} className="space-y-4">
                <p className="text-xs text-gray-500">Please set a new password to continue.</p>
                <div>
                  <FormPasswordInput label="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" required />
                  <PasswordRequirements password={newPassword} />
                </div>
                <FormButton loading={loading} disabled={!allMet(newPassword)}>{loading ? 'Setting password...' : 'Set New Password'}</FormButton>
              </form>
            )}

            {mode === 'verify' && (
              <form onSubmit={handleVerify} className="space-y-4">
                <p className="text-xs text-gray-500">Check your email for a verification code.</p>
                <FormInput label="Verification Code" type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="123456" required />
                <FormButton loading={loading}>{loading ? 'Verifying...' : 'Verify Email'}</FormButton>
                <button type="button" onClick={handleResendCode} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors">Resend code</button>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-xs text-gray-500">Enter your email and we'll send you a reset code.</p>
                <FormInput label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="netid@illinois.edu" required />
                <FormButton loading={loading}>{loading ? 'Sending code...' : 'Send Reset Code'}</FormButton>
                <button type="button" onClick={() => { setMode('signin'); reset(); }} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors">Back to sign in</button>
              </form>
            )}

            {mode === 'forgot-confirm' && (
              <form onSubmit={handleConfirmReset} className="space-y-4">
                <FormInput label="Reset Code" type="text" value={resetCode} onChange={e => setResetCode(e.target.value)} placeholder="123456" required />
                <div>
                  <FormPasswordInput label="New Password" value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)} placeholder="••••••••" required />
                  <PasswordRequirements password={resetNewPassword} />
                </div>
                <FormPasswordInput label="Confirm New Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" required />
                <FormButton loading={loading} disabled={!allMet(resetNewPassword)}>{loading ? 'Resetting...' : 'Reset Password'}</FormButton>
              </form>
            )}

            {/* Fix 1: onSkip wrapped in arrow function to avoid Illegal invocation error */}
            {/* Fix 2: handled by App.js reading sessionStorage for initialPath redirect */}
            {mode === 'mfa-nudge' && <MfaNudge onEnroll={handleEnrollMfa} onSkip={() => window.location.reload()} />}

          </div>
        </div>
      </div>
    </div>
  );
}